import { useEffect } from "react";

export default function useImageHandler(editorRef, patchNo, onChange) {
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    let resizing = null;

    const getRangeFromPoint = (x, y) => {
      if (document.caretRangeFromPoint)
        return document.caretRangeFromPoint(x, y);
      if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(x, y);
        if (!pos) return null;
        const range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
        return range;
      }
      return null;
    };

    const insertHTMLAtRange = (html, range) => {
      if (!range) return;
      const frag = range.createContextualFragment(html);
      const lastNode = frag.lastChild;
      range.insertNode(frag);
      const after = document.createRange();
      after.setStartAfter(lastNode);
      after.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(after);
      editor.focus();
    };

    const createImageBoxHTML = (src, width) => {
      const widthAttr = width ? ` style="width:${width}px"` : "";
      return `
        <span class="img-box" contenteditable="false" draggable="true">
          <img src="${src}"${widthAttr} />
          <span class="img-handle nw" data-dir="nw"></span>
          <span class="img-handle ne" data-dir="ne"></span>
          <span class="img-handle sw" data-dir="sw"></span>
          <span class="img-handle se" data-dir="se"></span>
        </span>
      `;
    };

    const handleFileInsert = async (file, rangeOrX, y) => {
      let range;

      if (typeof rangeOrX === "object" && rangeOrX instanceof Range) {
        range = rangeOrX;
      } else {
        range = getRangeFromPoint(rangeOrX, y);
      }

      if (!range) {
        const sel = window.getSelection();
        if (sel.rangeCount > 0) {
          range = sel.getRangeAt(0);
        } else {
          range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false);
        }
      }

      const blobUrl = URL.createObjectURL(file);
      insertHTMLAtRange(createImageBoxHTML(blobUrl), range);

      const formData = new FormData();
      formData.append("editorImage", file);
      formData.append("patchNo", patchNo);

      onChange?.(editor.innerHTML);
    };

    const onDrop = (e) => {
      e.preventDefault();
      const file = e.dataTransfer?.files?.[0];
      if (file && file.type.startsWith("image/")) {
        handleFileInsert(file, e.clientX, e.clientY);
      }
    };

    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            let range;
            const sel = window.getSelection();
            if (sel.rangeCount > 0) {
              range = sel.getRangeAt(0);
            } else {
              range = document.createRange();
              range.selectNodeContents(editorRef.current);
              range.collapse(false);
            }

            handleFileInsert(file, range);
          }
        }
      }
    };

    const onMouseDown = (e) => {
      const handle = e.target.closest?.(".img-handle");
      if (!handle) return;
      const box = handle.closest(".img-box");
      const img = box.querySelector("img");
      e.preventDefault();
      e.stopPropagation();

      const rect = img.getBoundingClientRect();
      resizing = {
        img,
        dir: handle.dataset.dir,
        startX: e.clientX,
        startW: rect.width,
        ratio: rect.width / rect.height,
      };
      document.body.style.userSelect = "none";
    };

    const onMouseMove = (e) => {
      if (!resizing) return;
      let dx = e.clientX - resizing.startX;
      let newW = resizing.startW;

      if (["se", "ne"].includes(resizing.dir)) {
        newW = resizing.startW + dx;
      } else {
        newW = resizing.startW - dx;
      }
      newW = Math.max(24, newW);
      resizing.img.style.width = `${newW}px`;
      resizing.img.style.height = "auto";
    };

    const onMouseUp = () => {
      if (resizing) {
        resizing = null;
        document.body.style.userSelect = "";
        onChange?.(editor.innerHTML);
      }
    };

    editor.addEventListener("drop", onDrop);
    editor.addEventListener("paste", onPaste);
    editor.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      editor.removeEventListener("drop", onDrop);
      editor.removeEventListener("paste", onPaste);
      editor.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [editorRef, patchNo, onChange]);
}